import { z } from "zod";
import { Form } from "@/components/ui/form";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { Diff } from "@/features/FormCard/Diff";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { useEditForm } from "@/features/Edit/hooks";
import { Tables } from "@/gen/tableNames/tables";
import { fieldTranslations } from "./translations";
import { useStrategyCurrency } from "./useStrategyCurrency";

const FormSchema = z.object({
  strategy_from_id: z.coerce.number().nullable().optional(),
  strategy_to_id: z.coerce.number().nullable().optional(),
  quantity: z.coerce.number().nullable().optional(),
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),
  weight: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  // Not rendered as inputs, but must stay declared here so Zod doesn't strip
  // them from the cached item data that <TechnicalInfoCard> reads.
  time_serie_file_id: z.coerce.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
const formSchema = FormSchema.keyof().enum;

export function C7PositionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation([Tables.C7_POSITIONS, "tables"]);

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    handleDelete,
  } = useEditForm({ id, client, table_name, FormSchema });

  const strategyFromId = form.watch("strategy_from_id");
  const strategyToId = form.watch("strategy_to_id");
  const strategyFromCurrency = useStrategyCurrency(strategyFromId);
  const strategyToCurrency = useStrategyCurrency(strategyToId);

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Position",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.strategy_from_id}
                    label={t(`columns.strategy_from_id`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Stratégie (depuis)",
                    })}
                    control={form.control}
                    endContent={
                      strategyFromCurrency ? (
                        <span className="text-xs text-default-400 whitespace-nowrap">
                          {strategyFromCurrency}
                        </span>
                      ) : undefined
                    }
                  />
                  <InputField
                    type="number"
                    name={formSchema.strategy_to_id}
                    label={t(`columns.strategy_to_id`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Stratégie (vers)",
                    })}
                    control={form.control}
                    endContent={
                      strategyToCurrency ? (
                        <span className="text-xs text-default-400 whitespace-nowrap">
                          {strategyToCurrency}
                        </span>
                      ) : undefined
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchema.date}
                    label={t(`columns.date`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.quantity}
                    label={t(`columns.quantity`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Quantité",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.weight}
                    label={t(`columns.weight`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Poids",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.market_value_local_ccy}
                    label={t(`columns.market_value_local_ccy`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Valeur marché (devise locale)",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.market_value_ptf_ccy}
                    label={t(`columns.market_value_ptf_ccy`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Valeur marché (devise ptf)",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? Diff(changedFields, [], fieldTranslations, table_name)
            : t("no_changes")}
        </ConfirmationModal>

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
